/**
 * Christmas design vote — voter identity and the vote API, no DOM.
 *
 * One vote per person is enforced twice: the browser remembers the vote
 * (localStorage + a first-party cookie, so clearing one is not enough), and the
 * Apps Script backend refuses a second vote from the same voter id or email.
 * A logged-in customer votes as "c-<customer id>", the same on every device;
 * anyone else is found again on a new device by their email key.
 */
class ChristmasVoteStore {
  static STORAGE_KEY = 'bt-christmas-vote-2026';
  static COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
  static REQUEST_TIMEOUT_MS = 15000;
  // Salt so the key matches no other site's hash of the same address.
  static EMAIL_KEY_SALT = 'bernese-christmas-vote-2026:';

  constructor({ endpoint, customerId }) {
    this.endpoint = endpoint;
    this.customerId = customerId;
  }

  /** The saved vote: `{ voterId, designId }`, or null before voting. */
  readVote() {
    const saved = this.currentVote || this.readLocal() || this.readCookie();
    if (saved && saved.designId) return saved;
    return null;
  }

  saveVote(designId, voterId) {
    this.currentVote = { voterId, designId };
    const record = JSON.stringify({ voterId, designId });
    try {
      localStorage.setItem(ChristmasVoteStore.STORAGE_KEY, record);
    } catch (error) {
      // Private mode can refuse storage: the cookie below still holds the vote.
    }
    try {
      document.cookie = `${ChristmasVoteStore.STORAGE_KEY}=${encodeURIComponent(record)}; path=/; max-age=${
        ChristmasVoteStore.COOKIE_MAX_AGE_SECONDS
      }; SameSite=Lax; Secure`;
    } catch (error) {
      // The current page still knows the confirmed vote when cookies are blocked.
    }
  }

  readResults() {
    try {
      return ChristmasVoteStore.parse(sessionStorage.getItem(`${ChristmasVoteStore.STORAGE_KEY}-results`));
    } catch (error) {
      return null;
    }
  }

  saveResults(results) {
    try {
      sessionStorage.setItem(`${ChristmasVoteStore.STORAGE_KEY}-results`, JSON.stringify(results));
    } catch (error) {
      // Fresh results can still be requested after Shopify returns.
    }
  }

  voterId() {
    if (this.customerId) return `c-${this.customerId}`;
    const saved = this.readVote();
    if (saved && saved.voterId) return saved.voterId;
    return ChristmasVoteStore.newUuid();
  }

  /** Current counts; with a customer or an email key also the vote cast elsewhere. */
  fetchResults(emailKey, returningVoter) {
    const params = new URLSearchParams();
    const saved = this.readVote();
    if (returningVoter || saved || this.customerId) params.set('voter', returningVoter || this.voterId());
    if (emailKey) params.set('email', emailKey);
    const url = new URL(this.endpoint);
    params.forEach((value, key) => url.searchParams.set(key, value));
    return this.request(url.toString(), { method: 'GET', cache: 'no-store' });
  }

  castVote(designId, emailKey) {
    const voterId = this.voterId();
    const source = new URLSearchParams(window.location.search).get('utm_source') || 'direct';
    return this.request(this.endpoint, {
      method: 'POST',
      // text/plain keeps this a "simple" CORS request: Apps Script answers no preflight.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ designId, voterId, emailKey, source }),
    }).then((response) => ({ ...response, voterId }));
  }

  async request(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ChristmasVoteStore.REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, redirect: 'follow' });
      if (!response.ok) throw new Error(`Vote service answered ${response.status}`);
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  readLocal() {
    try {
      return ChristmasVoteStore.parse(localStorage.getItem(ChristmasVoteStore.STORAGE_KEY));
    } catch (error) {
      return null;
    }
  }

  readCookie() {
    try {
      const prefix = `${ChristmasVoteStore.STORAGE_KEY}=`;
      const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
      return cookie ? ChristmasVoteStore.parse(decodeURIComponent(cookie.slice(prefix.length))) : null;
    } catch (error) {
      return null;
    }
  }

  static parse(value) {
    if (!value) return null;
    try {
      const record = JSON.parse(value);
      return record && typeof record === 'object' ? record : null;
    } catch (error) {
      return null;
    }
  }

  /** SHA-256 hex of the normalised email: the sheet never sees the address. */
  static async emailKey(email) {
    const normalised = ChristmasVoteStore.EMAIL_KEY_SALT + email.trim().toLowerCase();
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalised));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  static newUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}

window.ChristmasVoteStore = ChristmasVoteStore;
